import React, { useState, useEffect, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Plus, Pencil, Trash2, Users, Key, CheckSquare, FileText, Briefcase } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { getTaskUsers, createTaskUser, updateTaskUser, deleteTaskUser, resetUserPassword } from '../../services/taskApi';
import { MODULE_CATALOG, effectiveModules } from './modules';
import { toast } from 'sonner';

const DEPARTMENTS = [
  { value: '', label: '— None —', color: 'gray' },
  { value: 'sales_agent', label: 'Sales', color: 'blue' },
  { value: 'store_user', label: 'Store / Warehouse', color: 'amber' },
  { value: 'billing_user', label: 'Billing', color: 'cyan' },
  { value: 'purchase_user', label: 'Purchase', color: 'red' },
  { value: 'logistics_user', label: 'Logistics', color: 'pink' },
];

const DEPT_LABEL = Object.fromEntries(DEPARTMENTS.map(d => [d.value, d.label]));
const DEPT_COLOR = Object.fromEntries(DEPARTMENTS.map(d => [d.value, d.color]));

// Department-specific module hints (display only)
const DEPT_MODULES = {
  sales_agent: ['Tasks', 'Quotations', 'Sales Orders'],
  store_user: ['Tasks', 'Store/Tally', 'Pack & Push', 'Billing (view)'],
  billing_user: ['Tasks', 'Billing'],
  purchase_user: ['Tasks', 'Purchase Indents'],
  logistics_user: ['Tasks', 'Logistics & POD'],
};

const TaskUsers = () => {
  const { isDark } = useOutletContext();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [passwordDialog, setPasswordDialog] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', phone: '', password: '', role: 'member', flow_role: '', designation: '', modules: ['tasks'] });

  const fetchUsers = async () => {
    try { const res = await getTaskUsers(); setUsers(res.data || []); } catch { toast.error('Failed'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchUsers(); }, []);

  // Group users by department
  const grouped = useMemo(() => {
    const g = { admin: [], sales_agent: [], store_user: [], billing_user: [], purchase_user: [], logistics_user: [], none: [] };
    users.forEach(u => {
      if (u.role === 'admin') g.admin.push(u);
      else if (u.flow_role && g[u.flow_role]) g[u.flow_role].push(u);
      else g.none.push(u);
    });
    return g;
  }, [users]);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', email: '', phone: '', password: '', role: 'member', flow_role: '', designation: '', modules: ['tasks'] });
    setDialogOpen(true);
  };

  const openEdit = (u) => {
    setEditing(u);
    setForm({ name: u.name, email: u.email, phone: u.phone || '', password: '', role: u.role, flow_role: u.flow_role || '', designation: u.designation || '', modules: Array.from(effectiveModules(u)) });
    setDialogOpen(true);
  };

  const toggleModule = (key) => {
    setForm(f => ({ ...f, modules: f.modules.includes(key) ? f.modules.filter(m => m !== key) : [...f.modules, key] }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const permissions = { tasks: form.modules.includes('tasks'), quotations: form.modules.includes('quotations') };
    const payload = { name: form.name, email: form.email, phone: form.phone, role: form.role, flow_role: form.flow_role, designation: form.designation, modules: form.modules, permissions };
    try {
      if (editing) {
        await updateTaskUser(editing.id, payload);
        toast.success('User updated');
      } else {
        await createTaskUser({ ...payload, password: form.password });
        toast.success('User created');
      }
      setDialogOpen(false);
      fetchUsers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this user?')) return;
    try { await deleteTaskUser(id); toast.success('Deleted'); fetchUsers(); } catch { toast.error('Failed'); }
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) { toast.error('Min 6 characters'); return; }
    try { await resetUserPassword(passwordDialog.id, newPassword); toast.success('Password reset'); setPasswordDialog(null); setNewPassword(''); }
    catch { toast.error('Failed'); }
  };

  const cardCls = isDark ? 'border-0 bg-white/5' : 'border border-gray-200 bg-white shadow-sm';
  const dialogCls = isDark ? 'bg-[#0c1222] border-white/10 text-white' : 'bg-white border-gray-200 text-gray-900';
  const inputCls = isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-white border-gray-300 text-gray-900';
  const labelCls = isDark ? 'text-white/70' : 'text-gray-600';
  const selectCls = isDark ? 'bg-white/5 border-white/10 text-white' : 'bg-white border-gray-300 text-gray-900';
  const btnOutlineCls = isDark ? 'border-white/10 text-white hover:bg-white/5' : 'border-gray-300 text-gray-700 hover:bg-gray-100';

  return (
    <div className="space-y-6" data-testid="task-users-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Team Members</h1>
          <p className={`text-sm ${isDark ? 'text-white/40' : 'text-gray-500'}`}>{users.length} users</p>
        </div>
        <Button size="sm" onClick={openCreate} className="bg-blue-600 hover:bg-blue-700" data-testid="add-user-btn"><Plus size={14} className="mr-1" /> Add User</Button>
      </div>

      {loading ? <div className="space-y-3">{[...Array(3)].map((_, i) => <div key={i} className={`rounded-lg h-16 animate-pulse ${isDark ? 'bg-white/5' : 'bg-gray-200'}`} />)}</div> : (
        <div className="space-y-6">
          {[
            { key: 'admin', label: 'Admin Department', color: 'purple' },
            { key: 'sales_agent', label: 'Sales Department', color: 'blue' },
            { key: 'store_user', label: 'Store / Warehouse', color: 'amber' },
            { key: 'billing_user', label: 'Billing Department', color: 'cyan' },
            { key: 'purchase_user', label: 'Purchase Department', color: 'red' },
            { key: 'logistics_user', label: 'Logistics Department', color: 'pink' },
            { key: 'none', label: 'Unassigned', color: 'gray' },
          ].filter(g => grouped[g.key].length > 0).map(g => (
            <div key={g.key}>
              <div className={`flex items-center gap-2 mb-2 ${isDark ? 'text-white/60' : 'text-gray-600'}`}>
                <Briefcase size={14} className={`text-${g.color}-400`} />
                <h3 className="text-xs font-bold uppercase tracking-wider">{g.label}</h3>
                <span className={`text-[10px] ${isDark ? 'text-white/30' : 'text-gray-400'}`}>({grouped[g.key].length})</span>
              </div>
              <div className="space-y-2">
                {grouped[g.key].map(u => (
                  <Card key={u.id} className={`${cardCls} ${!u.is_active ? 'opacity-50' : ''}`} data-testid={`user-card-${u.id}`}>
                    <CardContent className="p-4 flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold flex-shrink-0 ${
                        isDark ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-600'
                      }`}>
                        {u.name?.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className={`text-sm font-medium truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>{u.name}</p>
                          <span className={`text-[10px] px-2 py-0.5 rounded-full capitalize ${isDark ? 'bg-white/10 text-white/50' : 'bg-gray-100 text-gray-600'}`}>{u.role}</span>
                          {u.flow_role && (
                            <span className={`text-[10px] px-2 py-0.5 rounded-full bg-${DEPT_COLOR[u.flow_role]}-500/15 text-${DEPT_COLOR[u.flow_role]}-400`}>
                              {DEPT_LABEL[u.flow_role]}
                            </span>
                          )}
                          {!u.is_active && <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-500/10 text-red-400">Inactive</span>}
                        </div>
                        <p className={`text-xs ${isDark ? 'text-white/40' : 'text-gray-500'}`}>{u.email}{u.designation ? ` | ${u.designation}` : ''}</p>
                        <div className="flex gap-2 mt-1 flex-wrap">
                          {u.permissions?.tasks && <span className="text-[9px] bg-blue-500/10 text-blue-400 px-1.5 py-0.5 rounded flex items-center gap-0.5"><CheckSquare size={9} /> Tasks</span>}
                          {u.permissions?.quotations && <span className="text-[9px] bg-indigo-500/10 text-indigo-400 px-1.5 py-0.5 rounded flex items-center gap-0.5"><FileText size={9} /> Quotations</span>}
                        </div>
                      </div>
                      <div className="flex gap-1.5 flex-shrink-0">
                        <Button size="sm" variant="ghost" className={`h-8 w-8 p-0 ${isDark ? 'text-white/40 hover:bg-white/5' : 'text-gray-400 hover:bg-gray-100'}`} onClick={() => { setPasswordDialog(u); setNewPassword(''); }} title="Reset Password"><Key size={14} /></Button>
                        <Button size="sm" variant="ghost" className={`h-8 w-8 p-0 ${isDark ? 'text-white/40 hover:bg-white/5' : 'text-gray-400 hover:bg-gray-100'}`} onClick={() => openEdit(u)} title="Edit"><Pencil size={14} /></Button>
                        <Button size="sm" variant="ghost" className="text-red-400 hover:bg-red-500/10 h-8 w-8 p-0" onClick={() => handleDelete(u.id)} title="Delete"><Trash2 size={14} /></Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className={`max-w-md ${dialogCls}`}>
          <DialogHeader><DialogTitle className={isDark ? 'text-white' : 'text-gray-900'}>{editing ? 'Edit' : 'Add'} User</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div><Label className={labelCls}>Name *</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required className={inputCls} data-testid="user-name-input" /></div>
            <div><Label className={labelCls}>Email *</Label><Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required className={inputCls} data-testid="user-email-input" /></div>
            <div><Label className={labelCls}>Phone</Label><Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className={inputCls} /></div>
            <div><Label className={labelCls}>Designation</Label><Input value={form.designation} onChange={e => setForm({ ...form, designation: e.target.value })} placeholder="e.g. Sales Manager" className={inputCls} data-testid="user-designation-input" /></div>
            {!editing && <div><Label className={labelCls}>Password *</Label><Input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} required minLength={6} className={inputCls} data-testid="user-password-input" /></div>}
            <div><Label className={labelCls}>Role</Label>
              <select className={`w-full h-9 rounded-md text-sm px-3 border ${selectCls}`} value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}>
                <option value="admin">Admin</option><option value="manager">Manager</option><option value="member">Member</option>
              </select></div>
            <div><Label className={labelCls}>Department (data scope)</Label>
              <select className={`w-full h-9 rounded-md text-sm px-3 border ${selectCls}`} value={form.flow_role} onChange={e => setForm({ ...form, flow_role: e.target.value })} data-testid="user-department">
                {DEPARTMENTS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
              </select>
              <p className={`text-[10px] mt-1 ${isDark ? 'text-white/40' : 'text-gray-500'}`}>
                Controls data rules (e.g. Sales sees only their own orders). Module access is set below.
              </p>
            </div>
            <div className="space-y-2">
              <Label className={labelCls}>Module Access</Label>
              <p className={`text-[10px] ${isDark ? 'text-white/40' : 'text-gray-500'}`}>Tick every module this user may open. Admins always see everything.</p>
              <div className="grid grid-cols-2 gap-x-4 gap-y-2 pt-1">
                {MODULE_CATALOG.map(m => (
                  <label key={m.key} className={`flex items-center gap-2 cursor-pointer text-sm ${isDark ? 'text-white/70' : 'text-gray-700'}`} data-testid={`module-row-${m.key}`}>
                    <input type="checkbox" className="h-4 w-4 rounded accent-blue-600" checked={form.modules.includes(m.key)} onChange={() => toggleModule(m.key)} data-testid={`module-checkbox-${m.key}`} />
                    <span>{m.label}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700" data-testid="user-submit-btn">{editing ? 'Update' : 'Create'}</Button>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className={btnOutlineCls}>Cancel</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!passwordDialog} onOpenChange={() => setPasswordDialog(null)}>
        <DialogContent className={`max-w-sm ${dialogCls}`}>
          <DialogHeader><DialogTitle className={isDark ? 'text-white' : 'text-gray-900'}>Reset Password</DialogTitle></DialogHeader>
          <p className={`text-sm ${isDark ? 'text-white/50' : 'text-gray-500'}`}>Reset password for {passwordDialog?.name}</p>
          <Input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="New password (min 6 chars)" className={inputCls} />
          <div className="flex gap-3">
            <Button onClick={handleResetPassword} className="flex-1 bg-blue-600 hover:bg-blue-700">Reset</Button>
            <Button variant="outline" onClick={() => setPasswordDialog(null)} className={btnOutlineCls}>Cancel</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TaskUsers;
